import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useEffect } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { Badge } from '@/common/ui/badge'
import { Switch } from '@/common/ui/switch'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Skeleton } from '@/common/ui/skeleton'
import useQueryGetIntervalMessageById from '../../api/hooks/useQueryGetIntervalMessageById'
import useMutationUpdateIntervalMessage from '../../api/hooks/useMutationUpdateIntervalMessage'
import { MessagingQueryKey } from '../../keys'
import { getConditionLabel, getChannelLabel } from '../../utils/conditionLabels'
import { formatIntervalSchedule } from '../../utils/intervalHelpers'
import { calculateCredit, getLanguageLabel } from '../../utils/smsCredits'
import { MessageChannel } from '../../types'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

import type { CreditData } from '../../utils/smsCredits'

const editIntervalMessageFormSchema = z.object({
	message: z.string().min(1, 'กรุณากรอกข้อความ').max(1000, 'ข้อความยาวเกินไป'),
	isActive: z.boolean(),
})

type EditIntervalMessageFormValues = z.infer<
	typeof editIntervalMessageFormSchema
>

const EditIntervalMessagePage = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const {
		data: intervalMessage,
		isLoading,
		isError,
	} = useQueryGetIntervalMessageById(id || '')
	const updateIntervalMessageMutation = useMutationUpdateIntervalMessage({
		onSuccess: () => {
			toast.success('อัปเดตข้อความอัตโนมัติตามช่วงเวลาสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [MessagingQueryKey.GetIntervalMessages],
			})
			queryClient.invalidateQueries({
				queryKey: [MessagingQueryKey.GetIntervalMessageById, id],
			})
			navigate('/operator/messaging/interval')
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการอัปเดตข้อความอัตโนมัติตามช่วงเวลา')
		},
	})

	const form = useForm<EditIntervalMessageFormValues>({
		resolver: zodResolver(editIntervalMessageFormSchema),
		defaultValues: {
			message: '',
			isActive: false,
		},
	})

	useEffect(() => {
		if (intervalMessage) {
			form.reset({
				message: intervalMessage.message,
				isActive: intervalMessage.isActive,
			})
		}
	}, [intervalMessage, form])

	const onSubmit = (values: EditIntervalMessageFormValues) => {
		if (!id) return

		updateIntervalMessageMutation.mutate({
			id,
			data: {
				message: values.message,
				isActive: values.isActive,
			},
		})
	}

	const message = form.watch('message') || ''

	const hasSMSChannel = intervalMessage?.intervalSmsTransaction !== null
	const hasLINEChannel = intervalMessage?.intervalLineTransaction !== null

	// Calculate SMS credits if SMS channel is present
	let creditData: CreditData | null = null
	try {
		if (hasSMSChannel && message) {
			creditData = calculateCredit(message)
		}
	} catch (error) {
		console.error('Credit calculation error:', error)
	}

	if (isLoading) {
		return (
			<PageContainer>
				<div className="space-y-6">
					<Skeleton className="h-10 w-64" />
					<Card>
						<CardHeader>
							<Skeleton className="h-6 w-48" />
						</CardHeader>
						<CardContent className="space-y-4">
							<Skeleton className="h-32 w-full" />
							<Skeleton className="h-32 w-full" />
							<Skeleton className="h-32 w-full" />
						</CardContent>
					</Card>
				</div>
			</PageContainer>
		)
	}

	if (isError || !intervalMessage) {
		return (
			<PageContainer>
				<div className="rounded-lg border p-8 text-center">
					<p className="text-muted-foreground">
						ไม่พบข้อความอัตโนมัติตามช่วงเวลา
					</p>
					<Button
						className="mt-4"
						onClick={() => navigate('/operator/messaging/interval')}
					>
						กลับไปหน้ารายการ
					</Button>
				</div>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold">
						แก้ไขข้อความอัตโนมัติตามช่วงเวลา
					</h1>
					<p className="mt-2 text-muted-foreground">
						แก้ไขเนื้อหาข้อความและสถานะการใช้งาน
					</p>
				</div>

				{/* Read-only Information Card */}
				<Card>
					<CardHeader>
						<CardTitle>ข้อมูลการตั้งค่า (อ่านอย่างเดียว)</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="space-y-2">
								<label className="text-sm font-medium text-muted-foreground">
									ช่วงเวลาส่ง
								</label>
								<p className="text-base">
									{formatIntervalSchedule(
										intervalMessage.intervalCondition,
										intervalMessage.intervalValue,
										intervalMessage.sendTime,
									)}
								</p>
							</div>
							<div className="space-y-2">
								<label className="text-sm font-medium text-muted-foreground">
									เงื่อนไขผู้รับ
								</label>
								<p className="text-base">
									{getConditionLabel(intervalMessage.messageGroupCondition)}
								</p>
							</div>
						</div>

						{(intervalMessage.minimum !== null ||
							intervalMessage.maximum !== null) && (
							<div className="space-y-2">
								<label className="text-sm font-medium text-muted-foreground">
									ค่าเงื่อนไข
								</label>
								<p className="text-base">
									{intervalMessage.minimum !== null &&
										`ค่าต่ำสุด: ${intervalMessage.minimum} `}
									{intervalMessage.maximum !== null &&
										`ค่าสูงสุด: ${intervalMessage.maximum}`}
								</p>
							</div>
						)}

						<div className="space-y-2">
							<label className="text-sm font-medium text-muted-foreground">
								ช่องทางการส่ง
							</label>
							<div className="flex gap-2">
								{hasSMSChannel && (
									<Badge variant="default">
										{getChannelLabel(MessageChannel.SMS)}
									</Badge>
								)}
								{hasLINEChannel && (
									<Badge variant="default" className="bg-green-600">
										{getChannelLabel(MessageChannel.LINE)}
									</Badge>
								)}
							</div>
						</div>

						<div className="space-y-2">
							<label className="text-sm font-medium text-muted-foreground">
								อนุญาตให้ส่งซ้ำ
							</label>
							<p className="text-base">
								{intervalMessage.allowResend ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
							</p>
						</div>

						<div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
							หมายเหตุ: ไม่สามารถแก้ไขการตั้งค่าช่วงเวลาและเงื่อนไขผู้รับได้
							เพื่อรักษาความถูกต้องของข้อมูล
						</div>
					</CardContent>
				</Card>

				{/* Editable Form Card */}
				<Card>
					<CardHeader>
						<CardTitle>แก้ไขเนื้อหาข้อความ</CardTitle>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
								className="space-y-6"
							>
								{/* IsActive Toggle */}
								<FormField
									control={form.control}
									name="isActive"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<FormLabel className="text-base">เปิดใช้งาน</FormLabel>
												<FormDescription>
													เปิด/ปิดการส่งข้อความอัตโนมัติตามช่วงเวลา
												</FormDescription>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
										</FormItem>
									)}
								/>

								{/* Message */}
								<FormField
									control={form.control}
									name="message"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ข้อความ</FormLabel>
											<FormControl>
												<Textarea
													placeholder="กรอกข้อความที่ต้องการส่ง"
													className="min-h-[120px]"
													{...field}
												/>
											</FormControl>
											<FormDescription>
												{hasSMSChannel && (
													<div className="space-y-1">
														{creditData ? (
															<>
																<div className="flex items-center justify-between text-sm">
																	<span>
																		{creditData.totalMessage -
																			creditData.messageLeft}{' '}
																		/ {creditData.totalMessage} ตัวอักษร (
																		{getLanguageLabel(creditData.lang)})
																	</span>
																	<span className="font-medium">
																		{creditData.credit} เครดิตต่อข้อความ
																	</span>
																</div>
															</>
														) : (
															<span className="text-sm">
																กรอกข้อความเพื่อดูจำนวนเครดิต SMS
															</span>
														)}
													</div>
												)}
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Submit buttons */}
								<div className="flex gap-3">
									<Button
										type="button"
										variant="outline"
										onClick={() => navigate('/operator/messaging/interval')}
									>
										ยกเลิก
									</Button>
									<Button
										type="submit"
										disabled={updateIntervalMessageMutation.isPending}
									>
										{updateIntervalMessageMutation.isPending
											? 'กำลังบันทึก...'
											: 'บันทึกการเปลี่ยนแปลง'}
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	)
}

const GuardedEditIntervalMessagePage = withUserPermission(
	EditIntervalMessagePage,
	{
		permissionType: PermissionType.Write,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedEditIntervalMessagePage as EditIntervalMessagePage }
